/**
 * Mock API Service
 * Provides mock implementations of API endpoints for development
 */

import {
  USE_MOCK_DATA,
  mockUsers,
  mockPatients,
  mockAuditAssignments,
  mockDashboardStats,
  mockRecentAudits,
  mockAuditLogs,
  mockDepartments,
  mockUnits,
  simulateApiDelay,
  createMockResponse,
  createMockError
} from './mockData';

// Mock Authentication
export const mockAuth = {
  login: async (username: string, password: string) => {
    await simulateApiDelay(800);
    
    const user = mockUsers.find(u => u.username === username);
    
    if (!user) {
      return createMockError('Invalid username or password');
    }
    
    // Simple password check for demo (in real app, this would be hashed)
    if (password !== 'admin123' && password !== 'password') {
      return createMockError('Invalid username or password');
    }
    
    return createMockResponse({
      user,
      token: `mock_token_${user.id}_${Date.now()}`,
      expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    });
  },

  logout: async () => {
    await simulateApiDelay(300);
    return createMockResponse({ message: 'Logged out successfully' });
  },

  getCurrentUser: async () => {
    await simulateApiDelay(200);
    // Return admin user for demo - consistent with login response structure
    return createMockResponse({ user: mockUsers[0] });
  }
};

// Mock Dashboard API
export const mockDashboard = {
  getStats: async () => {
    await simulateApiDelay(600);
    return createMockResponse(mockDashboardStats);
  },

  getRecentAudits: async (params?: any) => {
    await simulateApiDelay(400);
    
    let filteredAudits = [...mockRecentAudits];
    
    // Apply pagination if provided
    const page = params?.page || 1;
    const limit = params?.limit || 10;
    const offset = (page - 1) * limit;
    
    const total = filteredAudits.length;
    const paginatedAudits = filteredAudits.slice(offset, offset + limit);
    
    return {
      success: true,
      data: paginatedAudits,
      message: 'Recent audits retrieved successfully',
      pagination: {
        page,
        limit,
        total,
        pages: Math.ceil(total / limit)
      }
    };
  }
};

// Mock Patients API
export const mockPatientsApi = {
  getPatients: async (params?: any) => {
    await simulateApiDelay(500);
    
    let filteredPatients = [...mockPatients];
    
    // Apply filters if provided
    if (params?.search) {
      const searchTerm = params.search.toLowerCase();
      filteredPatients = filteredPatients.filter(patient =>
        patient.patient_id.toLowerCase().includes(searchTerm) ||
        patient.cause_of_death.toLowerCase().includes(searchTerm) ||
        patient.final_diagnosis.toLowerCase().includes(searchTerm)
      );
    }
    
    if (params?.status) {
      filteredPatients = filteredPatients.filter(patient =>
        patient.audit_status === params.status
      );
    }
    
    if (params?.department) {
      filteredPatients = filteredPatients.filter(patient =>
        patient.dept === params.department
      );
    }
    
    return {
      success: true,
      patients: filteredPatients,
      message: 'Success',
      pagination: {
        page: params?.page || 1,
        limit: params?.limit || 10,
        total: filteredPatients.length,
        pages: Math.ceil(filteredPatients.length / (params?.limit || 10))
      }
    };
  },

  getPatient: async (id: string) => {
    await simulateApiDelay(300);
    const patient = mockPatients.find(p => p.patient_id === id);
    
    if (!patient) {
      return createMockError('Patient not found');
    }
    
    return createMockResponse(patient);
  },

  createMortalityCase: async (data: any) => {
    await simulateApiDelay(1000);
    
    // Create a new mock patient with the provided data
    const newPatient = {
      id: mockPatients.length + 1,
      ...data,
      created_time: new Date().toISOString(),
      audit_status: 'pending',
      is_audit_required: true
    };
    
    // Add to mock patients array
    mockPatients.push(newPatient);
    
    return createMockResponse({
      id: newPatient.id,
      message: 'Manual mortality case created successfully'
    });
  },
  
  createMorbidityCase: async (data: any) => {
    await simulateApiDelay(1000);
    
    // Simulate creating a new morbidity case
    const newCaseId = Math.max(...mockPatients.map(p => p.id), 0) + 1;
    
    return createMockResponse({
      message: 'Morbidity case created successfully',
      data: {
        id: newCaseId,
        patient_id: data.patient_id
      }
    });
  },

  updatePatient: async (id: number, data: any) => {
    await simulateApiDelay(800);
    
    // Find the patient to update
    const patientIndex = mockPatients.findIndex(p => p.id === id);
    if (patientIndex === -1) {
      return createMockResponse({
        success: false,
        message: 'Patient not found'
      });
    }
    
    // Update the patient data
    mockPatients[patientIndex] = {
      ...mockPatients[patientIndex],
      ...data,
      id: id // Ensure ID doesn't change
    };
    
    return createMockResponse({
      success: true,
      message: 'Patient updated successfully',
      data: mockPatients[patientIndex]
    });
  }
};

// Mock Users API
export const mockUsersApi = {
  getUsers: async (params?: any) => {
    await simulateApiDelay(400);
    let filteredUsers = [...mockUsers];
    
    // Apply filters
    if (params?.role) {
      filteredUsers = filteredUsers.filter(user => user.role === params.role);
    }
    if (params?.department_id) {
      filteredUsers = filteredUsers.filter(user => user.department_id === params.department_id);
    }
    if (params?.unit_id) {
      filteredUsers = filteredUsers.filter(user => user.unit_id === params.unit_id);
    }
    if (params?.is_active !== undefined) {
      filteredUsers = filteredUsers.filter(user => user.is_active === params.is_active);
    }
    if (params?.search) {
      const searchTerm = params.search.toLowerCase();
      filteredUsers = filteredUsers.filter(user => 
        user.first_name.toLowerCase().includes(searchTerm) ||
        user.last_name.toLowerCase().includes(searchTerm) ||
        user.username.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm)
      );
    }
    
    return createMockResponse(filteredUsers);
  },

  getUser: async (id: number) => {
    await simulateApiDelay(300);
    const user = mockUsers.find(u => u.id === id);
    if (!user) {
      return createMockError('User not found');
    }
    return createMockResponse(user);
  },

  getUserProfile: async () => {
    await simulateApiDelay(200);
    // Return the first user as current user for demo
    return createMockResponse(mockUsers[0]);
  },

  createUser: async (userData: any) => {
    await simulateApiDelay(600);
    const newUser = {
      id: mockUsers.length + 1,
      ...userData,
      created_at: new Date().toISOString(),
      is_active: true
    };
    mockUsers.push(newUser);
    return createMockResponse({ id: newUser.id });
  },

  updateUser: async (id: number, userData: any) => {
    await simulateApiDelay(500);
    const userIndex = mockUsers.findIndex(u => u.id === id);
    
    if (userIndex === -1) {
      return createMockError('User not found');
    }
    
    mockUsers[userIndex] = { ...mockUsers[userIndex], ...userData };
    return createMockResponse({ message: 'User updated successfully' });
  },

  changePassword: async (data: any) => {
    await simulateApiDelay(400);
    const { user_id, current_password, new_password } = data;
    
    if (!user_id || !new_password) {
      return createMockError('User ID and new password are required');
    }
    
    // Mock password change
    return createMockResponse({ message: 'Password changed successfully' });
  },

  toggleUserStatus: async (data: any) => {
    await simulateApiDelay(300);
    const { user_id, is_active } = data;
    
    if (!user_id || is_active === undefined) {
      return createMockError('User ID and active status are required');
    }
    
    const userIndex = mockUsers.findIndex(u => u.id === user_id);
    if (userIndex === -1) {
      return createMockError('User not found');
    }
    
    mockUsers[userIndex].is_active = is_active;
    return createMockResponse({ message: 'User status updated successfully' });
  },

  deleteUser: async (id: number) => {
    await simulateApiDelay(400);
    const userIndex = mockUsers.findIndex(u => u.id === id);
    
    if (userIndex === -1) {
      return createMockError('User not found');
    }
    
    mockUsers.splice(userIndex, 1);
    return createMockResponse({ message: 'User deleted successfully' });
  },

  assignAudits: async (assignmentData: any) => {
    await simulateApiDelay(1000);
    
    const { case_ids, case_type, assignment_type, auditor_id, team_id, reviewer_id, due_date, priority, notes } = assignmentData;
    
    // Create mock assignments
    const newAssignments = case_ids.map((caseId: number, index: number) => ({
      id: Math.max(...mockAuditAssignments.map(a => a.id)) + index + 1,
      patient_id: `P${caseId.toString().padStart(9, '0')}`,
      auditor_id: auditor_id,
      auditor_name: mockUsers.find(u => u.id === auditor_id)?.first_name + ' ' + mockUsers.find(u => u.id === auditor_id)?.last_name || 'Unknown Auditor',
      assigned_date: new Date().toISOString(),
      due_date: due_date,
      status: 'assigned',
      priority: priority,
      notes: notes
    }));
    
    mockAuditAssignments.push(...newAssignments);
    
    return createMockResponse({
      message: `Successfully assigned ${case_ids.length} ${case_type} cases`,
      assigned_count: case_ids.length,
      assignments: newAssignments
    });
  }
};

// Mock Audit API
export const mockAuditApi = {
  getAudits: async () => {
    await simulateApiDelay(500);
    return createMockResponse(mockAuditAssignments);
  },

  getMyAudits: async (params?: any) => {
    await simulateApiDelay(500);
    // Filter assignments for current user (assuming user ID 1 for demo)
    let myAudits = mockAuditAssignments.map(assignment => ({
      id: assignment.id,
      assignment_id: assignment.id,
      case_type: assignment.case_type,
      case_id: assignment.case_id,
      patient_id: assignment.patient_id,
      patient_name: `Patient ${assignment.patient_id}`,
      status: assignment.status,
      priority: assignment.priority,
      assigned_date: assignment.assigned_date,
      due_date: assignment.due_date,
      progress_percentage: assignment.progress_percentage || 0,
      case_summary: `Case summary for ${assignment.patient_id}`,
      primary_diagnosis: 'Primary diagnosis placeholder',
      department_name: 'Sample Department',
      unit_name: 'Sample Unit',
      reviewer_name: assignment.reviewer_name,
      notes: assignment.notes,
      days_remaining: Math.ceil((new Date(assignment.due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
      is_overdue: new Date(assignment.due_date) < new Date() && assignment.status !== 'approved'
    }));
    
    // Apply pagination if provided
    const page = params?.page || 1;
    const limit = params?.limit || 20;
    const offset = (page - 1) * limit;
    
    const total = myAudits.length;
    const paginatedAudits = myAudits.slice(offset, offset + limit);
    
    return {
      success: true,
      data: paginatedAudits,
      message: 'My audits retrieved successfully',
      pagination: {
        page,
        limit,
        total,
        pages: Math.ceil(total / limit)
      }
    };
  },

  getAudit: async (id: string) => {
    await simulateApiDelay(300);
    const audit = mockAuditAssignments.find(a => a.id.toString() === id);
    
    if (!audit) {
      return createMockError('Audit not found');
    }
    
    // Mock form data
    const mockFormData = {
      case_information: {
        patient_ip_number: 'IP123456',
        age: 67,
        admission_date: '2024-03-14T08:15:00Z',
        discharge_date: '2024-03-15T14:30:00Z',
        primary_diagnosis: 'Acute Myocardial Infarction',
        cause_of_death: 'Cardiogenic shock'
      },
      case_summary: 'Patient presented with acute STEMI and developed cardiogenic shock despite aggressive intervention.',
      issues_revealed: 'Delay in recognition of cardiogenic shock, inadequate fluid management',
      recommendations: [
        {
          recommendation: 'Implement early warning system for cardiogenic shock',
          priority: 'high',
          responsible_party: 'Cardiology Department',
          due_date: '2024-04-15T00:00:00Z'
        }
      ],
      audit_team: [
        {
          name: 'Dr. Sarah Smith',
          role: 'Primary Auditor',
          signature: 'data:image/png;base64,mock_signature_data'
        }
      ]
    };
    
    return createMockResponse({
      ...audit,
      // For SupervisorReview component
      audit: audit,
      patient: mockPatients.find(p => p.patient_id === audit.patient_id),
      form_data: mockFormData,
      // For AuditForm component (backward compatibility)
      form: mockFormData,
      patient_data: mockPatients.find(p => p.patient_id === audit.patient_id)
    });
  },

  submitAudit: async (auditData: any) => {
    await simulateApiDelay(800);
    
    // Update audit status
    const auditIndex = mockAuditAssignments.findIndex(a => a.id === auditData.id);
    if (auditIndex !== -1) {
      mockAuditAssignments[auditIndex].status = 'submitted';
    }
    
    return createMockResponse({
      message: 'Audit submitted successfully',
      audit_id: auditData.id
    });
  },

  saveDraft: async (auditData: any) => {
    await simulateApiDelay(500);
    
    // Calculate mock progress
    const totalSections = 11;
    let completedSections = 0;
    
    if (auditData.patient_ip_number && auditData.age) completedSections++;
    if (auditData.case_summary) completedSections++;
    if (auditData.documentation_questions?.every((q: any) => q.answer && q.comment)) completedSections++;
    if (auditData.diagnosis_questions?.every((q: any) => q.answer && q.comment)) completedSections++;
    if (auditData.management_questions?.every((q: any) => q.answer && q.comment)) completedSections++;
    if (auditData.outcome_questions?.every((q: any) => q.answer && q.comment)) completedSections++;
    if (auditData.disclosure_questions?.every((q: any) => q.answer && q.comment)) completedSections++;
    if (auditData.second_victim_questions?.every((q: any) => q.answer && q.comment)) completedSections++;
    if (auditData.issues_revealed) completedSections++;
    if (auditData.recommendations?.length > 0) completedSections++;
    if (auditData.audit_team?.length > 0) completedSections++;
    
    const progressPercentage = Math.round((completedSections / totalSections) * 100);
    
    return createMockResponse({
      message: 'Draft saved successfully',
      audit_id: auditData.id || Math.floor(Math.random() * 1000) + 1,
      progress_percentage: progressPercentage,
      last_saved: new Date().toISOString()
    });
  },

  updateAudit: async (auditData: any) => {
    await simulateApiDelay(600);
    
    return createMockResponse({
      message: 'Audit updated successfully by supervisor',
      audit_id: auditData.audit_id,
      updated_by: 'supervisor'
    });
  },

  approveAudit: async (auditId: string, reviewNotes: string) => {
    await simulateApiDelay(600);
    
    const auditIndex = mockAuditAssignments.findIndex(a => a.id.toString() === auditId);
    if (auditIndex !== -1) {
      mockAuditAssignments[auditIndex].status = 'approved';
    }
    
    return createMockResponse({
      message: 'Audit approved successfully',
      audit_id: auditId
    });
  },

  rejectAudit: async (auditId: string, reviewNotes: string) => {
    await simulateApiDelay(600);
    
    const auditIndex = mockAuditAssignments.findIndex(a => a.id.toString() === auditId);
    if (auditIndex !== -1) {
      mockAuditAssignments[auditIndex].status = 'rejected';
    }
    
    return createMockResponse({
      message: 'Audit rejected and returned for revision',
      audit_id: auditId
    });
  }
};


// Mock Notifications API
export const mockNotificationsApi = {
  getNotifications: async (params?: any) => {
    await simulateApiDelay(300);
    
    const mockNotifications = [
      {
        id: 1,
        type: 'audit_revision_requested',
        title: 'Audit Revision Required',
        message: 'A reviewer has requested revisions to your audit. Please review the feedback and make the necessary changes.',
        data: {
          audit_id: 1,
          reviewer_name: 'Dr. Sarah Smith',
          review_notes: 'Please provide more details on the medication errors section.',
          revision_count: 1
        },
        is_read: false,
        created_at: new Date().toISOString()
      },
      {
        id: 2,
        type: 'new_assignment',
        title: 'New Audit Assignment',
        message: 'You have been assigned a new audit case to review.',
        data: {
          audit_id: 2,
          case_type: 'mortality',
          patient_id: '123456789'
        },
        is_read: true,
        created_at: new Date(Date.now() - 86400000).toISOString() // 1 day ago
      }
    ];
    
    let filteredNotifications = [...mockNotifications];
    
    if (params?.page && params?.limit) {
      const offset = (params.page - 1) * params.limit;
      filteredNotifications = filteredNotifications.slice(offset, offset + params.limit);
    }
    
    return createMockResponse({
      notifications: filteredNotifications,
      pagination: {
        page: params?.page || 1,
        limit: params?.limit || 20,
        total: mockNotifications.length,
        pages: Math.ceil(mockNotifications.length / (params?.limit || 20))
      }
    });
  },

  getUnreadCount: async () => {
    await simulateApiDelay(200);
    return createMockResponse({ unread_count: 1 });
  },

  markAsRead: async (notificationId: number) => {
    await simulateApiDelay(300);
    return createMockResponse({ message: 'Notification marked as read' });
  },

  markAllAsRead: async () => {
    await simulateApiDelay(300);
    return createMockResponse({ message: 'All notifications marked as read' });
  }
};

// Mock Audit Logs API
export const mockAuditLogsApi = {
  getAuditLogs: async (params?: any) => {
    await simulateApiDelay(400);
    
    let filteredLogs = [...mockAuditLogs];
    
    if (params?.userId) {
      filteredLogs = filteredLogs.filter(log => log.userId === params.userId);
    }
    
    if (params?.action) {
      filteredLogs = filteredLogs.filter(log => log.action === params.action);
    }
    
    if (params?.entityType) {
      filteredLogs = filteredLogs.filter(log => log.entityType === params.entityType);
    }
    
    return createMockResponse({
      logs: filteredLogs,
      total: filteredLogs.length
    });
  },

  getAuditLog: async (id: number) => {
    await simulateApiDelay(300);
    const log = mockAuditLogs.find(l => l.id === id);
    if (!log) {
      return createMockError('Audit log not found');
    }
    return createMockResponse(log);
  },

  createAuditLog: async (data: any) => {
    await simulateApiDelay(500);
    const newLog = {
      id: mockAuditLogs.length + 1,
      userId: data.user_id || 1,
      userName: 'Current User',
      action: data.action_type || 'create',
      entityType: data.entity_type || 'audit',
      entityId: data.entity_id || '1',
      details: {
        ip_address: data.ip_address || '127.0.0.1',
        user_agent: data.user_agent || 'Mozilla/5.0',
        ...data.metadata
      },
      timestamp: new Date(),
      ipAddress: data.ip_address || '127.0.0.1',
      userAgent: data.user_agent || 'Mozilla/5.0'
    };
    mockAuditLogs.push(newLog);
    return createMockResponse({ id: newLog.id });
  },

  bulkCreateAuditLogs: async (data: any) => {
    await simulateApiDelay(800);
    const createdIds = [];
    for (const logData of data.logs) {
      const newLog = {
        id: mockAuditLogs.length + 1,
        userId: logData.user_id || 1,
        userName: 'Current User',
        action: logData.action_type || 'create',
        entityType: logData.entity_type || 'audit',
        entityId: logData.entity_id || '1',
        details: {
          ip_address: logData.ip_address || '127.0.0.1',
          user_agent: logData.user_agent || 'Mozilla/5.0',
          ...logData.metadata
        },
        timestamp: new Date(),
        ipAddress: logData.ip_address || '127.0.0.1',
        userAgent: logData.user_agent || 'Mozilla/5.0'
      };
      mockAuditLogs.push(newLog);
      createdIds.push(newLog.id);
    }
    return createMockResponse({ created_ids: createdIds });
  },

  deleteAuditLog: async (id: number) => {
    await simulateApiDelay(300);
    const index = mockAuditLogs.findIndex(l => l.id === id);
    if (index === -1) {
      return createMockError('Audit log not found');
    }
    mockAuditLogs.splice(index, 1);
    return createMockResponse({ message: 'Audit log deleted successfully' });
  },

  clearOldAuditLogs: async (data: any) => {
    await simulateApiDelay(600);
    const daysOld = data.days_old || 365;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    
    const initialCount = mockAuditLogs.length;
    // Remove old logs by splicing from the end to avoid index issues
    for (let i = mockAuditLogs.length - 1; i >= 0; i--) {
      if (new Date(mockAuditLogs[i].timestamp) < cutoffDate) {
        mockAuditLogs.splice(i, 1);
      }
    }
    
    const deletedCount = initialCount - mockAuditLogs.length;
    return createMockResponse({ 
      message: `Cleared ${deletedCount} old audit logs`,
      deleted_count: deletedCount
    });
  },

  exportAuditLogs: async (params?: any) => {
    await simulateApiDelay(500);
    let filteredLogs = [...mockAuditLogs];
    
    if (params?.date_from) {
      const fromDate = new Date(params.date_from);
      filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) >= fromDate);
    }
    
    if (params?.date_to) {
      const toDate = new Date(params.date_to);
      filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) <= toDate);
    }
    
    return createMockResponse({
      data: filteredLogs,
      export_info: {
        format: params?.format || 'json',
        date_from: params?.date_from,
        date_to: params?.date_to,
        exported_at: new Date().toISOString(),
        total_records: filteredLogs.length
      }
    });
  }
};


// Mock EMR API
export const mockEMRApi = {
  fetchPatient: async (patientId: string) => {
    await simulateApiDelay(800);
    
    const patient = mockPatients.find(p => p.patient_id === patientId);
    
    if (!patient) {
      return createMockError('Patient not found in EMR system');
    }
    
    return createMockResponse(patient);
  },
  
  fetchMortalityCases: async () => {
    await simulateApiDelay(1200);
    return createMockResponse(mockPatients);
  },
  
  syncPatient: async (patientId: string) => {
    await simulateApiDelay(1000);
    
    const patient = mockPatients.find(p => p.patient_id === patientId);
    
    if (!patient) {
      return createMockError('Patient not found for sync');
    }
    
    return createMockResponse({
      message: 'Patient synced successfully',
      patient_id: patient.id
    });
  },
  
  
  syncAll: async () => {
    await simulateApiDelay(2000);
    
    return createMockResponse({
      message: `Synced ${mockPatients.length} patients successfully`,
      synced_count: mockPatients.length,
      errors: []
    });
  }
};

// Mock Departments API
export const mockDepartmentsApi = {
  getDepartments: async () => {
    await simulateApiDelay(500);
    return createMockResponse(mockDepartments);
  },
  
  getDepartment: async (id: number) => {
    await simulateApiDelay(300);
    const department = mockDepartments.find(d => d.id === id);
    if (!department) {
      return createMockError('Department not found');
    }
    return createMockResponse(department);
  },
  
  createDepartment: async (data: any) => {
    await simulateApiDelay(600);
    const newDepartment = {
      id: Math.max(...mockDepartments.map(d => d.id)) + 1,
      name: data.name,
      description: data.description || '',
      emr_code: data.emr_code || 0,
      is_active: data.is_active !== undefined ? data.is_active : 1,
      created_at: new Date().toISOString()
    };
    mockDepartments.push(newDepartment);
    return createMockResponse(newDepartment);
  },
  
  updateDepartment: async (id: number, data: any) => {
    await simulateApiDelay(500);
    const index = mockDepartments.findIndex(d => d.id === id);
    if (index === -1) {
      return createMockError('Department not found');
    }
    mockDepartments[index] = { ...mockDepartments[index], ...data };
    return createMockResponse(mockDepartments[index]);
  },
  
  deleteDepartment: async (id: number) => {
    await simulateApiDelay(400);
    const index = mockDepartments.findIndex(d => d.id === id);
    if (index === -1) {
      return createMockError('Department not found');
    }
    mockDepartments.splice(index, 1);
    return createMockResponse({ message: 'Department deleted successfully' });
  }
};

// Mock Units API
export const mockUnitsApi = {
  getUnits: async (departmentId?: number) => {
    await simulateApiDelay(500);
    let units = mockUnits;
    
    if (departmentId) {
      units = units.filter(unit => unit.department_id === departmentId);
    }
    
    const unitsWithDepartments = units.map(unit => {
      const department = mockDepartments.find(d => d.id === unit.department_id);
      return {
        ...unit,
        department_name: department ? department.name : 'Unknown Department'
      };
    });
    return createMockResponse(unitsWithDepartments);
  },
  
  getUnitsByDepartment: async (departmentId: number) => {
    await simulateApiDelay(300);
    const units = mockUnits.filter(u => u.department_id === departmentId && u.is_active === 1);
    const unitsWithDepartments = units.map(unit => {
      const department = mockDepartments.find(d => d.id === unit.department_id);
      return {
        ...unit,
        department_name: department ? department.name : 'Unknown Department'
      };
    });
    return createMockResponse(unitsWithDepartments);
  },
  
  getUnit: async (id: number) => {
    await simulateApiDelay(300);
    const unit = mockUnits.find(u => u.id === id);
    if (!unit) {
      return createMockError('Unit not found');
    }
    const department = mockDepartments.find(d => d.id === unit.department_id);
    return createMockResponse({
      ...unit,
      department_name: department ? department.name : 'Unknown Department'
    });
  },
  
  createUnit: async (data: any) => {
    await simulateApiDelay(600);
    const newUnit = {
      id: Math.max(...mockUnits.map(u => u.id)) + 1,
      name: data.name,
      emr_code: data.emr_code || 0,
      department_id: data.department_id,
      description: data.description || '',
      is_active: data.is_active !== undefined ? data.is_active : 1,
      created_at: new Date().toISOString()
    };
    mockUnits.push(newUnit);
    return createMockResponse(newUnit);
  },
  
  updateUnit: async (id: number, data: any) => {
    await simulateApiDelay(500);
    const index = mockUnits.findIndex(u => u.id === id);
    if (index === -1) {
      return createMockError('Unit not found');
    }
    mockUnits[index] = { ...mockUnits[index], ...data };
    return createMockResponse(mockUnits[index]);
  },
  
  deleteUnit: async (id: number) => {
    await simulateApiDelay(400);
    const index = mockUnits.findIndex(u => u.id === id);
    if (index === -1) {
      return createMockError('Unit not found');
    }
    mockUnits.splice(index, 1);
    return createMockResponse({ message: 'Unit deleted successfully' });
  }
};

// Mock Recommendations API
export const mockRecommendationsApi = {
  getCategories: async () => {
    await simulateApiDelay(500);
    return createMockResponse([
      { id: 1, name: 'Documentation', description: 'Improvements to medical documentation', color: '#3B82F6' },
      { id: 2, name: 'Clinical Practice', description: 'Changes to clinical practices', color: '#10B981' },
      { id: 3, name: 'Communication', description: 'Enhancements to communication protocols', color: '#F59E0B' },
      { id: 4, name: 'Training', description: 'Staff training and education', color: '#8B5CF6' },
      { id: 5, name: 'Equipment', description: 'Equipment and technology improvements', color: '#EF4444' },
      { id: 6, name: 'Policy', description: 'Policy and protocol updates', color: '#6B7280' }
    ]);
  },
  
  createRecommendation: async (data: any) => {
    await simulateApiDelay(800);
    return createMockResponse({
      id: Math.floor(Math.random() * 1000) + 1,
      ...data,
      created_at: new Date().toISOString()
    });
  },
  
  updateRecommendation: async (id: number, data: any) => {
    await simulateApiDelay(600);
    return createMockResponse({
      id,
      ...data,
      updated_at: new Date().toISOString()
    });
  },
  
  getRecommendations: async (auditFormId?: number) => {
    await simulateApiDelay(700);
    const recommendations = [
      {
        id: 1,
        audit_form_id: auditFormId || 1,
        category_id: 1,
        title: 'Improve Documentation Standards',
        description: 'Implement standardized documentation templates',
        priority: 'high',
        status: 'pending',
        due_date: '2024-02-15',
        created_at: new Date().toISOString()
      }
    ];
    return createMockResponse(auditFormId ? recommendations.filter(r => r.audit_form_id === auditFormId) : recommendations);
  }
};

// Mock Teams API
const mockTeamsApi = {
  getTeams: async () => {
    await simulateApiDelay(500);
    const teams = [
      {
        id: 1,
        name: 'Cardiology Audit Team',
        description: 'Specialized in cardiac and cardiovascular audits',
        lead_auditor_id: 4,
        lead_auditor_name: 'Emily Wilson',
        department_id: 2,
        department_name: 'Cardiology',
        member_count: 3,
        is_active: true,
        created_at: '2024-01-15T00:00:00Z'
      },
      {
        id: 2,
        name: 'Surgery Review Team',
        description: 'Comprehensive surgical case reviews',
        lead_auditor_id: 7,
        lead_auditor_name: 'Dr. Michael Chen',
        department_id: 5,
        department_name: 'Surgery',
        member_count: 4,
        is_active: true,
        created_at: '2024-01-20T00:00:00Z'
      }
    ];
    return createMockResponse(teams);
  },

  getTeam: async (id: number) => {
    await simulateApiDelay(300);
    const team = {
      id: id,
      name: 'Cardiology Audit Team',
      description: 'Specialized in cardiac and cardiovascular audits',
      lead_auditor_id: 4,
      lead_auditor_name: 'Emily Wilson',
      department_id: 2,
      department_name: 'Cardiology',
      is_active: true,
      created_at: '2024-01-15T00:00:00Z'
    };
    return createMockResponse(team);
  },

  createTeam: async (data: any) => {
    await simulateApiDelay(600);
    const newTeam = {
      id: Math.floor(Math.random() * 1000) + 100,
      name: data.name,
      description: data.description || '',
      lead_auditor_id: data.lead_auditor_id,
      department_id: data.department_id || null,
      is_active: true,
      created_at: new Date().toISOString()
    };
    return createMockResponse(newTeam);
  },

  updateTeam: async (id: number, data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({ id, ...data });
  },

  deleteTeam: async (id: number) => {
    await simulateApiDelay(400);
    return createMockResponse({ id });
  },

  getTeamMembers: async (teamId: number) => {
    await simulateApiDelay(400);
    const members = [
      {
        id: 1,
        team_id: teamId,
        user_id: 4,
        user_name: 'Emily Wilson',
        role: 'lead_auditor',
        joined_at: '2024-01-15T00:00:00Z',
        is_active: 1
      },
      {
        id: 2,
        team_id: teamId,
        user_id: 7,
        user_name: 'Dr. Michael Chen',
        role: 'auditor',
        joined_at: '2024-01-16T00:00:00Z',
        is_active: 1
      },
      {
        id: 3,
        team_id: teamId,
        user_id: 8,
        user_name: 'Sarah Johnson',
        role: 'reviewer',
        joined_at: '2024-01-17T00:00:00Z',
        is_active: 1
      }
    ];
    return createMockResponse(members);
  },

  addTeamMember: async (data: any) => {
    await simulateApiDelay(500);
    const newMember = {
      id: Math.floor(Math.random() * 1000) + 100,
      team_id: data.team_id,
      user_id: data.user_id,
      role: data.role,
      joined_at: new Date().toISOString(),
      is_active: 1
    };
    return createMockResponse(newMember);
  },

  removeTeamMember: async (memberId: number) => {
    await simulateApiDelay(400);
    return createMockResponse({ id: memberId });
  },

  getAvailableUsers: async () => {
    await simulateApiDelay(400);
    const users = [
      {
        id: 4,
        username: 'emily.wilson',
        first_name: 'Emily',
        last_name: 'Wilson',
        email: 'emily.wilson@hospital.com',
        role: 'auditor',
        department_name: 'Cardiology',
        unit_name: 'Cardiac ICU',
        is_active: true
      },
      {
        id: 7,
        username: 'michael.chen',
        first_name: 'Dr. Michael',
        last_name: 'Chen',
        email: 'michael.chen@hospital.com',
        role: 'reviewer',
        department_name: 'Surgery',
        unit_name: 'Operating Room',
        is_active: true
      },
      {
        id: 8,
        username: 'sarah.johnson',
        first_name: 'Sarah',
        last_name: 'Johnson',
        email: 'sarah.johnson@hospital.com',
        role: 'auditor',
        department_name: 'Internal Medicine',
        unit_name: 'Medical ICU',
        is_active: true
      }
    ];
    return createMockResponse(users);
  }
};

// Main Mock API Object
// Mock Reviews API
export const mockReviewsApi = {
  getReviews: async (params?: any) => {
    await simulateApiDelay(400);
    const reviews = [
      {
        id: 1,
        audit_form_id: 1,
        reviewer_id: 2,
        review_notes: 'Comprehensive audit with good documentation',
        assignment_status: 'approved',
        reviewed_at: '2024-01-15T10:30:00Z',
        created_at: '2024-01-15T09:00:00Z',
        patient_id: 'P000000001',
        case_type: 'mortality',
        audit_status: 'approved',
        auditor_name: 'Dr. John Smith'
      },
      {
        id: 2,
        audit_form_id: 2,
        reviewer_id: 2,
        review_notes: 'Needs additional documentation',
        assignment_status: 'rejected',
        reviewed_at: '2024-01-16T14:20:00Z',
        created_at: '2024-01-16T13:00:00Z',
        patient_id: 'P000000002',
        case_type: 'morbidity',
        audit_status: 'rejected',
        auditor_name: 'Dr. Jane Doe'
      }
    ];
    
    let filteredReviews = [...reviews];
    
    if (params?.status) {
      filteredReviews = filteredReviews.filter(review => review.assignment_status === params.status);
    }
    if (params?.reviewer_id) {
      filteredReviews = filteredReviews.filter(review => review.reviewer_id === params.reviewer_id);
    }
    
    return createMockResponse(filteredReviews);
  },

  getReview: async (id: number) => {
    await simulateApiDelay(300);
    const audit = {
      id: 1,
      case_type: 'mortality',
      case_id: 1,
      assigned_to_id: 2,
      reviewer_id: 3,
      status: 'submitted',
      priority: 'high',
      due_date: '2024-01-20',
      assigned_date: '2024-01-15T09:00:00Z',
      patient_id: 'P000000001',
      time_of_death: '2024-01-14T15:30:00Z',
      cause_of_death: 'Cardiac arrest',
      final_diagnosis: 'Acute myocardial infarction',
      auditor_name: 'Dr. John Smith',
      form_id: id,
      form_status: 'submitted',
      submitted_at: '2024-01-15T10:00:00Z',
      review_status: 'approved',
      review_notes: 'Comprehensive audit with good documentation',
      reviewed_at: '2024-01-15T10:30:00Z'
    };
    
    const patient = {
      id: 'P000000001',
      patient_id: 'P000000001',
      note_no: 'N12345',
      time_of_death: '2024-01-14T15:30:00Z',
      cause_of_death: 'Cardiac arrest',
      final_diagnosis: 'Acute myocardial infarction',
      dept: 'Cardiology',
      unit_id: 'CCU'
    };
    
    const formData = {
      case_information: {
        patient_ip_number: 'P000000001',
        age: 65,
        admission_date: '2024-01-10T08:00:00Z',
        discharge_date: '2024-01-14T15:30:00Z',
        length_of_stay: 4,
        cause_of_death: 'Cardiac arrest',
        source_of_admission: 'Emergency Department',
        primary_diagnosis: 'Acute myocardial infarction'
      },
      documentation_questions: [
        { id: 'doc_001', question: 'Was the documentation adequate?', answer: 'yes', comment: 'Well documented' }
      ],
      diagnosis_questions: [
        { id: 'diag_001', question: 'Was there a delay in diagnosis?', answer: 'no', comment: 'Timely diagnosis' }
      ],
      management_questions: [
        { id: 'mgmt_001', question: 'Was treatment appropriate?', answer: 'yes', comment: 'Standard protocol followed' }
      ],
      outcome_questions: [
        { id: 'outcome_001', question: 'Was the outcome preventable?', answer: 'no', comment: 'Unavoidable outcome' }
      ]
    };
    
    return createMockResponse({
      audit: audit,
      patient: patient,
      form_data: formData
    });
  },

  getMyReviews: async () => {
    await simulateApiDelay(400);
    const myReviews = [
      {
        id: 1,
        audit_form_id: 1,
        reviewer_id: 2,
        review_notes: 'Comprehensive audit with good documentation',
        assignment_status: 'approved',
        reviewed_at: '2024-01-15T10:30:00Z',
        created_at: '2024-01-15T09:00:00Z',
        patient_id: 'P000000001',
        case_type: 'mortality',
        audit_status: 'approved',
        auditor_name: 'Dr. John Smith'
      }
    ];
    return createMockResponse(myReviews);
  },

  getPendingReviews: async () => {
    await simulateApiDelay(400);
    const pendingReviews = [
      {
        audit_form_id: 3,
        patient_id: 'P000000003',
        case_type: 'mortality',
        audit_status: 'submitted',
        submitted_at: '2024-01-17T08:00:00Z',
        auditor_name: 'Dr. Mike Johnson',
        review_id: null,
        assignment_status: null
      }
    ];
    return createMockResponse(pendingReviews);
  },

  getReviewStatistics: async () => {
    await simulateApiDelay(300);
    const stats = {
      overview: {
        total_reviews: 25,
        approved_reviews: 18,
        rejected_reviews: 5,
        pending_reviews: 2,
        avg_review_time_hours: 4.5
      },
      monthly: [
        { month: '2024-01', review_count: 8, approved_count: 6 },
        { month: '2024-02', review_count: 12, approved_count: 9 },
        { month: '2024-03', review_count: 5, approved_count: 3 }
      ]
    };
    return createMockResponse(stats);
  },

  submitReview: async (data: any) => {
    await simulateApiDelay(600);
    return createMockResponse({ message: 'Review submitted successfully' });
  },

  assignReview: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({ 
      message: 'Review assigned successfully',
      id: Math.floor(Math.random() * 1000) + 1
    });
  },

  reassignReview: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({ message: 'Review reassigned successfully' });
  },

  updateReview: async (data: any) => {
    await simulateApiDelay(400);
    return createMockResponse({ message: 'Review updated successfully' });
  },

  approveReview: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({ message: 'Review approved successfully' });
  },

  rejectReview: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({ message: 'Review rejected successfully' });
  },

  deleteReview: async (id: number) => {
    await simulateApiDelay(300);
    return createMockResponse({ message: 'Review deleted successfully' });
  },

  getReviewers: async (auditFormId: number) => {
    await simulateApiDelay(600);
    
    // Mock multiple reviewers for the audit form
    const mockReviewers = [
      {
        id: 1,
        reviewer_id: 5,
        reviewer_name: 'Dr. Sarah Johnson',
        role: 'primary',
        status: 'completed',
        assignment_status: 'approved',
        review_notes: 'Comprehensive audit with good documentation. Minor suggestions provided.',
        reviewed_at: '2024-01-15T10:30:00Z',
        priority: 'high',
        notes: 'Primary reviewer'
      },
      {
        id: 2,
        reviewer_id: 6,
        reviewer_name: 'Dr. Michael Chen',
        role: 'secondary',
        status: 'completed',
        assignment_status: 'approved',
        review_notes: 'Agree with primary review. Well-structured audit process.',
        reviewed_at: '2024-01-15T14:20:00Z',
        priority: 'medium',
        notes: 'Secondary reviewer'
      },
      {
        id: 3,
        reviewer_id: 7,
        reviewer_name: 'Dr. Emily Rodriguez',
        role: 'secondary',
        status: 'pending',
        assignment_status: 'pending',
        review_notes: '',
        reviewed_at: null,
        priority: 'low',
        notes: 'Specialized secondary review'
      }
    ];

    const summary = {
      total_reviewers: mockReviewers.length,
      completed_reviews: mockReviewers.filter(r => r.status === 'completed').length,
      pending_reviews: mockReviewers.filter(r => r.status === 'pending').length,
      approved_count: mockReviewers.filter(r => r.assignment_status === 'approved').length,
      rejected_count: mockReviewers.filter(r => r.assignment_status === 'rejected').length,
      needs_revision_count: mockReviewers.filter(r => r.assignment_status === 'needs_revision').length,
      consensus_reached: true,
      final_status: 'approved' as const
    };

    return createMockResponse({
      reviewers: mockReviewers,
      summary: summary
    });
  },

  submitIndividualReview: async (data: any) => {
    await simulateApiDelay(800);
    
    return createMockResponse({
      message: 'Individual review submitted successfully',
      review_id: Math.floor(Math.random() * 1000) + 1,
      consensus_status: 'pending'
    });
  }
};

// Mock Admin API
export const mockAdminApi = {
  getEMRSyncStatus: async () => {
    await simulateApiDelay(300);
    return createMockResponse({
      last_sync_time: '2025-09-20 14:30:00',
      total_synced_from_emr: 45,
      total_mortality_cases: 45,
      audited_cases: 12,
      emr_imported_cases: 45,
      manually_added_cases: 0,
      pending_audit_cases: 33
    });
  },
  
  smartEMRSync: async (syncData: any) => {
    await simulateApiDelay(2000);
    return createMockResponse({
      imported_count: 4,
      skipped_count: 0,
      total_processed: 4,
      errors: [],
      last_sync_time: new Date().toISOString(),
      from_date: syncData.from_date || '2025-09-10 00:00:00',
      to_date: syncData.to_date || null
    });
  },

  getEnhancedEMRData: async (month: number, year: number) => {
    await simulateApiDelay(1500);
    
    // Generate mock EMR data
    const patients = [];
    const unitIds = [525,570,571,567,568,582,25025,26009,26010,26036,26043,26044];
    const departments = ['ICU', 'Emergency', 'Cardiology', 'Neurology', 'Surgery'];
    const causes = ['Cardiac Arrest', 'Respiratory Failure', 'Sepsis', 'Stroke', 'Trauma'];
    const places = ['Hospital', 'Home', 'Emergency Room', 'ICU', 'Operating Room'];
    const unitNames = ['ICU Unit', 'Emergency Department', 'Cardiology Ward', 'Neurology Unit', 'Surgery Ward'];
    
    const patientCount = Math.floor(Math.random() * 15) + 10; // 10-25 patients
    
    for (let i = 0; i < patientCount; i++) {
      const day = Math.floor(Math.random() * 28) + 1;
      const hour = Math.floor(Math.random() * 24);
      const minute = Math.floor(Math.random() * 60);
      const unitIndex = Math.floor(Math.random() * unitIds.length);
      
      patients.push({
        NOTE_NO: `NOTE${String(i + 1).padStart(6, '0')}`,
        NOTE_DATE: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        PATIENTID: `P${String(Math.floor(Math.random() * 900000) + 100000)}`,
        VISITID: `V${String(Math.floor(Math.random() * 900000) + 100000)}`,
        TRF_ID: `TRF${String(Math.floor(Math.random() * 9000) + 1000)}`,
        AGE: Math.floor(Math.random() * 77) + 18,
        TIME_OF_DEATH: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`,
        PLACE_OF_DEATH: places[Math.floor(Math.random() * places.length)],
        PLACE_DESCRIPTION: 'Hospital ward',
        CAUSE_OF_DEATH: causes[Math.floor(Math.random() * causes.length)],
        CREATED_TIME: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}:00`,
        CAUSEDEATH_DIRECT: causes[Math.floor(Math.random() * causes.length)],
        CAUSEDEATH_UNDERLYING1: causes[Math.floor(Math.random() * causes.length)],
        CAUSEDEATH_UNDERLYING2: causes[Math.floor(Math.random() * causes.length)],
        CAUSEDEATH_UNDERLYING3: causes[Math.floor(Math.random() * causes.length)],
        INTERVAL_DIRECT: `${Math.floor(Math.random() * 24) + 1} hours`,
        INTERVAL_UNDERLYING1: `${Math.floor(Math.random() * 30) + 1} days`,
        INTERVAL_UNDERLYING2: `${Math.floor(Math.random() * 365) + 1} days`,
        INTERVAL_UNDERLYING3: `${Math.floor(Math.random() * 365) + 1} days`,
        INTERVAL_OTHER: `${Math.floor(Math.random() * 7) + 1} days`,
        MANNER_OF_DEATH: ['Natural', 'Accident', 'Suicide', 'Homicide'][Math.floor(Math.random() * 4)],
        INJURY_TYPE: ['None', 'Blunt', 'Sharp', 'Burn'][Math.floor(Math.random() * 4)],
        DEPT: departments[Math.floor(Math.random() * departments.length)],
        UNITID: [525,570,571,567,568,582,25025,26009,26010,26036,26043,26044][Math.floor(Math.random() * 12)],
        VISIT_TIME: `${year}-${String(month).padStart(2, '0')}-${String(Math.floor(Math.random() * day) + 1).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}:00`,
        TRF_DATE: `${year}-${String(month).padStart(2, '0')}-${String(Math.floor(Math.random() * day) + 1).padStart(2, '0')}`,
        DISCHARGE_DATE: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        PROV_DIAGNOSIS: `Provisional diagnosis ${i + 1}`,
        FINAL_DIAGNOSIS: `Final diagnosis ${i + 1}`,
        unit_name: unitNames[unitIndex],
        unit_id: unitIds[unitIndex]
       
      });
    }
    
    // Calculate statistics
    const unitBreakdown: { [key: string]: number } = {};
    patients.forEach(patient => {
      unitBreakdown[patient.unit_name] = (unitBreakdown[patient.unit_name] || 0) + 1;
    });
    
    return createMockResponse({
      patients,
      statistics: {
        total_patients: patients.length,
        unit_breakdown: unitBreakdown
      },
      month,
      year
    });
  },

  importSelectedPatients: async (patients: any[]) => {
    await simulateApiDelay(2000);
    
    const imported = Math.floor(patients.length * 0.8); // 80% success rate
    const skipped = patients.length - imported;
    
    return createMockResponse({
      imported,
      skipped,
      errors: []
    });
  },
  
  updatePatientAuditRequired: async (patientId: number, isAuditRequired: boolean) => {
    await simulateApiDelay(500);
    
    return createMockResponse({
      success: true,
      message: "Audit required status updated successfully"
    });
  },
  
  updateMorbidityCaseAuditRequired: async (caseId: number, isAuditRequired: boolean) => {
    await simulateApiDelay(500);
    
    return createMockResponse({
      success: true,
      message: "Audit required status updated successfully"
    });
  },
  
  deleteMortalityCase: async (caseId: number) => {
    await simulateApiDelay(1000);
    
    // Simulate some cases where deletion might fail
    if (caseId === 999) {
      return createMockResponse({
        success: false,
        message: "Cannot delete case with existing audit assignments"
      });
    }
    
    return createMockResponse({
      success: true,
      message: "Mortality case deleted successfully"
    });
  }
};

// Mock Assignment API
export const mockAssignmentsApi = {
  getAssignmentAuditors: async (assignmentId: number) => {
    await simulateApiDelay(500);
    
    // Mock auditors for the assignment
    const mockAuditors = [
      {
        id: 1,
        auditor_id: 2,
        auditor_name: 'Dr. John Smith',
        email: 'john.smith@hospital.com',
        user_role: 'auditor',
        auditor_role: 'primary',
        status: 'active',
        notes: 'Lead auditor for this case',
        assigned_at: '2024-01-15T10:00:00Z'
      },
      {
        id: 2,
        auditor_id: 3,
        auditor_name: 'Dr. Jane Doe',
        email: 'jane.doe@hospital.com',
        user_role: 'superuser',
        auditor_role: 'secondary',
        status: 'assigned',
        notes: 'Supporting auditor',
        assigned_at: '2024-01-15T10:05:00Z'
      },
      {
        id: 3,
        auditor_id: 4,
        auditor_name: 'Dr. Mike Johnson',
        email: 'mike.johnson@hospital.com',
        user_role: 'auditor',
        auditor_role: 'secondary',
        status: 'assigned',
        notes: 'Specialized secondary',
        assigned_at: '2024-01-15T10:10:00Z'
      }
    ];

    return createMockResponse({
      auditors: mockAuditors,
      total_auditors: mockAuditors.length,
      primary_auditors: mockAuditors.filter(a => a.auditor_role === 'primary').length,
      secondary_auditors: mockAuditors.filter(a => a.auditor_role === 'secondary').length
    });
  },

  updateAuditorStatus: async (data: any) => {
    await simulateApiDelay(400);
    
    return createMockResponse({
      message: 'Auditor status updated successfully',
      auditor_id: data.auditor_id,
      new_status: data.status,
      assignment_completed: false
    });
  },

  updateAssignment: async (data: any) => {
    await simulateApiDelay(600);
    
    // Find and update the assignment in mock data
    const assignmentIndex = mockAuditAssignments.findIndex(a => a.id === data.id);
    if (assignmentIndex !== -1) {
      mockAuditAssignments[assignmentIndex] = {
        ...mockAuditAssignments[assignmentIndex],
        ...data
      };
    }
    
    return createMockResponse({
      message: 'Assignment updated successfully',
      assignment_id: data.id
    });
  },

  getAssignments: async (params?: any) => {
    await simulateApiDelay(600);
    
    // Use the existing mockAuditAssignments data
    let assignments = [...mockAuditAssignments];
    
    // Apply filters if provided
    if (params?.status && params.status !== 'all') {
      assignments = assignments.filter(a => a.status === params.status);
    }
    
    if (params?.priority && params.priority !== 'all') {
      assignments = assignments.filter(a => a.priority === params.priority);
    }
    
    if (params?.type && params.type !== 'all') {
      assignments = assignments.filter(a => a.assignment_type === params.type);
    }
    
    if (params?.search) {
      const searchTerm = params.search.toLowerCase();
      assignments = assignments.filter(a => 
        a.assigned_to_name.toLowerCase().includes(searchTerm) ||
        a.patient_id.toLowerCase().includes(searchTerm) ||
        (a.case_type === 'mortality' && a.cause_of_death?.toLowerCase().includes(searchTerm)) ||
        (a.case_type === 'morbidity' && a.case_summary?.toLowerCase().includes(searchTerm))
      );
    }
    
    // Pagination
    const page = params?.page || 1;
    const limit = params?.limit || 20;
    const offset = (page - 1) * limit;
    const total = assignments.length;
    const paginatedAssignments = assignments.slice(offset, offset + limit);
    
    return createMockResponse({
      data: paginatedAssignments,
      pagination: {
        page,
        limit,
        total,
        pages: Math.ceil(total / limit)
      }
    });
  },

  deleteAssignment: async (assignmentId: number) => {
    await simulateApiDelay(500);
    
    // Find the assignment in mock data
    const assignmentIndex = mockAuditAssignments.findIndex(a => a.id === assignmentId);
    if (assignmentIndex === -1) {
      return createMockResponse({
        success: false,
        message: 'Assignment not found'
      });
    }
    
    // Check if assignment has form record (simulate this check)
    const assignment = mockAuditAssignments[assignmentIndex];
    if (assignment.has_form_record) {
      return createMockResponse({
        success: false,
        message: 'Cannot delete assignment with existing form record'
      });
    }
    
    // Remove the assignment from mock data
    mockAuditAssignments.splice(assignmentIndex, 1);
    
    return createMockResponse({
      success: true,
      message: 'Assignment deleted successfully'
    });
  }
};

// Mock Morbidity Categories API
export const mockMorbidityCategoriesApi = {
  getCategories: async () => {
    await simulateApiDelay(300);
    const categories = [
      { id: 1, name: 'Healthcare-associated infection (HAI)', description: 'Infections acquired during healthcare delivery', is_active: 1 },
      { id: 2, name: 'Medication error', description: 'Errors in medication prescribing, dispensing, or administration', is_active: 1 },
      { id: 3, name: 'Surgical complication', description: 'Complications arising from surgical procedures', is_active: 1 },
      { id: 4, name: 'Diagnostic error', description: 'Errors in diagnosis or diagnostic procedures', is_active: 1 },
      { id: 5, name: 'Patient safety incident', description: 'General patient safety incidents', is_active: 1 },
      { id: 6, name: 'Equipment failure', description: 'Medical equipment malfunctions or failures', is_active: 1 },
      { id: 7, name: 'Communication error', description: 'Errors in communication between healthcare providers', is_active: 1 },
      { id: 8, name: 'Other', description: 'Other types of morbidity cases', is_active: 1 }
    ];
    return createMockResponse(categories);
  },
  
  createCategory: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({
      message: 'Morbidity category created successfully',
      data: { id: Math.floor(Math.random() * 1000) + 100 }
    });
  },
  
  updateCategory: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({
      message: 'Morbidity category updated successfully'
    });
  },
  
  deleteCategory: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({
      message: 'Morbidity category deleted successfully'
    });
  }
};

// Mock Morbidity Audit Indications API
export const mockMorbidityAuditIndicationsApi = {
  getIndications: async () => {
    await simulateApiDelay(300);
    const indications = [
      { id: 1, name: 'High severity case', description: 'Cases with high severity or significant impact', is_active: 1 },
      { id: 2, name: 'Recurring pattern', description: 'Cases showing recurring patterns or trends', is_active: 1 },
      { id: 3, name: 'Quality improvement initiative', description: 'Cases identified for quality improvement', is_active: 1 },
      { id: 4, name: 'Regulatory requirement', description: 'Cases requiring audit due to regulatory requirements', is_active: 1 },
      { id: 5, name: 'Patient/family complaint', description: 'Cases involving patient or family complaints', is_active: 1 },
      { id: 6, name: 'Staff concern', description: 'Cases raised by staff concerns', is_active: 1 },
      { id: 7, name: 'Sentinel event', description: 'Cases involving sentinel events', is_active: 1 },
      { id: 8, name: 'Other', description: 'Other indications for audit', is_active: 1 }
    ];
    return createMockResponse(indications);
  },
  
  createIndication: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({
      message: 'Morbidity audit indication created successfully',
      data: { id: Math.floor(Math.random() * 1000) + 100 }
    });
  },
  
  updateIndication: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({
      message: 'Morbidity audit indication updated successfully'
    });
  },
  
  deleteIndication: async (data: any) => {
    await simulateApiDelay(500);
    return createMockResponse({
      message: 'Morbidity audit indication deleted successfully'
    });
  }
};

// Mock Revision History API
export const mockRevisionHistoryApi = {
  getAssignmentRevisions: async (assignmentId: number) => {
    await simulateApiDelay(400);
    
    const mockRevisions = [
      {
        id: 1,
        assignment_id: assignmentId,
        revision_number: 1,
        reviewer_id: 2,
        review_notes: 'Please provide more details on the medication errors section.',
        assignment_status: 'needs_revision',
        created_at: '2024-01-15T10:30:00Z',
        reviewer_name: 'Dr. Sarah Smith',
        reviewer_role: 'reviewer'
      },
      {
        id: 2,
        assignment_id: assignmentId,
        revision_number: 2,
        reviewer_id: 3,
        review_notes: 'Additional documentation required for the diagnosis section.',
        assignment_status: 'needs_revision',
        created_at: '2024-01-16T14:20:00Z',
        reviewer_name: 'Dr. Michael Chen',
        reviewer_role: 'reviewer'
      }
    ];
    
    const assignmentDetails = {
      id: assignmentId,
      case_type: 'mortality',
      case_id: 1,
      assigned_to_id: 1,
      reviewer_id: 2,
      revision_count: 2,
      last_revision_date: '2024-01-16T14:20:00Z',
      auditor_name: 'Dr. John Smith',
      reviewer_name: 'Dr. Sarah Smith',
      patient_id: 'P000000001'
    };
    
    return createMockResponse({
      assignment: assignmentDetails,
      revisions: mockRevisions,
      total_revisions: mockRevisions.length
    });
  },

  getRevisionHistory: async (params?: any) => {
    await simulateApiDelay(500);
    
    const mockRevisions = [
      {
        id: 1,
        assignment_id: 1,
        revision_number: 1,
        reviewer_id: 2,
        review_notes: 'Please provide more details on the medication errors section.',
        assignment_status: 'needs_revision',
        created_at: '2024-01-15T10:30:00Z',
        reviewer_name: 'Dr. Sarah Smith',
        reviewer_role: 'reviewer',
        case_type: 'mortality',
        case_id: 1,
        auditor_name: 'Dr. John Smith',
        patient_id: 'P000000001'
      },
      {
        id: 2,
        assignment_id: 2,
        revision_number: 1,
        reviewer_id: 3,
        review_notes: 'Additional documentation required for the diagnosis section.',
        assignment_status: 'needs_revision',
        created_at: '2024-01-16T14:20:00Z',
        reviewer_name: 'Dr. Michael Chen',
        reviewer_role: 'reviewer',
        case_type: 'morbidity',
        case_id: 2,
        auditor_name: 'Dr. Jane Doe',
        patient_id: 'P000000002'
      }
    ];
    
    let filteredRevisions = [...mockRevisions];
    
    if (params?.assignment_id) {
      filteredRevisions = filteredRevisions.filter(r => r.assignment_id === params.assignment_id);
    }
    
    if (params?.reviewer_id) {
      filteredRevisions = filteredRevisions.filter(r => r.reviewer_id === params.reviewer_id);
    }
    
    if (params?.review_status) {
      filteredRevisions = filteredRevisions.filter(r => r.assignment_status === params.assignment_status);
    }
    
    // Pagination
    const page = params?.page || 1;
    const limit = params?.limit || 20;
    const offset = (page - 1) * limit;
    const total = filteredRevisions.length;
    const paginatedRevisions = filteredRevisions.slice(offset, offset + limit);
    
    return createMockResponse({
      revisions: paginatedRevisions,
      pagination: {
        page,
        limit,
        total,
        pages: Math.ceil(total / limit)
      }
    });
  },

  getRevisionDetails: async (revisionId: number) => {
    await simulateApiDelay(300);
    
    const revision = {
      id: revisionId,
      assignment_id: 1,
      revision_number: 1,
      reviewer_id: 2,
      review_notes: 'Please provide more details on the medication errors section.',
      review_status: 'needs_revision',
      created_at: '2024-01-15T10:30:00Z',
      reviewer_name: 'Dr. Sarah Smith',
      reviewer_role: 'reviewer',
      case_type: 'mortality',
      case_id: 1,
      assigned_to_id: 1,
      auditor_name: 'Dr. John Smith',
      patient_id: 'P000000001'
    };
    
    return createMockResponse({ revision });
  },

  createRevisionEntry: async (data: any) => {
    await simulateApiDelay(600);
    
    const newRevision = {
      id: Math.floor(Math.random() * 1000) + 100,
      assignment_id: data.assignment_id,
      revision_number: data.revision_number || 1,
      reviewer_id: data.reviewer_id,
      review_notes: data.review_notes,
      assignment_status: data.assignment_status,
      created_at: new Date().toISOString()
    };
    
    return createMockResponse({
      message: 'Revision entry created successfully',
      revision_number: newRevision.revision_number
    });
  }
};

export const mockApi = {
  auth: mockAuth,
  dashboard: mockDashboard,
  patients: mockPatientsApi,
  users: mockUsersApi,
  audit: mockAuditApi,
  notifications: mockNotificationsApi,
  auditLogs: mockAuditLogsApi,
  departments: mockDepartmentsApi,
  units: mockUnitsApi,
  emr: mockEMRApi,
  recommendations: mockRecommendationsApi,
  teams: mockTeamsApi,
  reviews: mockReviewsApi,
  assignments: mockAssignmentsApi,
  morbidityCategories: mockMorbidityCategoriesApi,
  morbidityAuditIndications: mockMorbidityAuditIndicationsApi,
  admin: mockAdminApi,
  revisionHistory: mockRevisionHistoryApi,

  // Recommendation Follow-up mock data
  recommendationFollowup: {
    getDashboard: async () => {
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        success: true as const,
        data: {
          total_recommendations: 45,
          overdue_count: 8,
          status_breakdown: {
            pending: 12,
            in_progress: 18,
            completed: 13,
            cancelled: 2
          },
          priority_breakdown: {
            critical: 5,
            high: 12,
            medium: 20,
            low: 8
          },
          category_breakdown: [
            { name: 'Documentation', color: '#3B82F6', count: 15 },
            { name: 'Clinical Practice', color: '#10B981', count: 12 },
            { name: 'Communication', color: '#F59E0B', count: 8 },
            { name: 'Training', color: '#8B5CF6', count: 6 },
            { name: 'Equipment', color: '#EF4444', count: 3 },
            { name: 'Policy', color: '#6B7280', count: 1 }
          ],
          recent_activity: [
            {
              id: 1,
              title: 'Improve documentation standards',
              status: 'completed',
              updated_at: '2024-01-15T10:30:00Z',
              updated_by_name: 'Dr. Sarah Johnson',
              patient_id: 'P001234'
            },
            {
              id: 2,
              title: 'Update communication protocols',
              status: 'in_progress',
              updated_at: '2024-01-15T09:15:00Z',
              updated_by_name: 'Dr. Michael Chen',
              patient_id: 'P001235'
            }
          ]
        },
        message: 'Dashboard data retrieved successfully'
      };
    },

    getRecommendations: async (params: string) => {
      await new Promise(resolve => setTimeout(resolve, 300));
      const mockRecommendations = [
        {
          id: 1,
          title: 'Improve documentation standards',
          description: 'Implement standardized documentation templates for all patient records',
          priority: 'high',
          status: 'in_progress',
          category_name: 'Documentation',
          category_color: '#3B82F6',
          assigned_to_name: 'Dr. Sarah Johnson',
          assigned_to_email: 'sarah.johnson@hospital.com',
          due_date: '2024-02-15',
          due_status: 'on_track',
          patient_id: 'P001234',
          case_type: 'mortality',
          created_at: '2024-01-10T08:00:00Z',
          updated_at: '2024-01-15T10:30:00Z',
          next_followup_date: '2024-01-25',
          days_until_next_followup: 3
        },
        {
          id: 2,
          title: 'Update communication protocols',
          description: 'Revise inter-departmental communication procedures',
          priority: 'critical',
          status: 'pending',
          category_name: 'Communication',
          category_color: '#F59E0B',
          assigned_to_name: 'Dr. Michael Chen',
          assigned_to_email: 'michael.chen@hospital.com',
          due_date: '2024-01-20',
          due_status: 'overdue',
          patient_id: 'P001235',
          case_type: 'mortality',
          created_at: '2024-01-05T14:00:00Z',
          updated_at: '2024-01-05T14:00:00Z',
          next_followup_date: '2024-01-22',
          days_until_next_followup: -1
        },
        {
          id: 3,
          title: 'Staff training on new procedures',
          description: 'Conduct training sessions for all nursing staff',
          priority: 'medium',
          status: 'completed',
          category_name: 'Training',
          category_color: '#8B5CF6',
          assigned_to_name: 'Nurse Manager Lisa',
          assigned_to_email: 'lisa.manager@hospital.com',
          due_date: '2024-01-30',
          due_status: 'on_track',
          patient_id: 'P001236',
          case_type: 'morbidity',
          created_at: '2024-01-08T11:00:00Z',
          updated_at: '2024-01-12T16:45:00Z',
          next_followup_date: null,
          days_until_next_followup: null
        }
      ];

      return {
        success: true as const,
        data: mockRecommendations,
        pagination: {
          page: 1,
          limit: 20,
          total: mockRecommendations.length,
          pages: 1
        },
        message: 'Recommendations retrieved successfully'
      };
    },

    getRecommendationDetails: async (id: number) => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return {
        success: true as const,
        data: {
          id: id,
          title: 'Improve documentation standards',
          description: 'Implement standardized documentation templates for all patient records',
          priority: 'high',
          status: 'in_progress',
          category_name: 'Documentation',
          category_color: '#3B82F6',
          assigned_to_name: 'Dr. Sarah Johnson',
          assigned_to_email: 'sarah.johnson@hospital.com',
          due_date: '2024-02-15',
          patient_id: 'P001234',
          case_type: 'mortality',
          created_at: '2024-01-10T08:00:00Z',
          updated_at: '2024-01-15T10:30:00Z',
          action_plan: '1. Review current documentation templates\n2. Identify gaps and inconsistencies\n3. Develop standardized templates\n4. Train staff on new templates\n5. Implement templates across all departments\n6. Monitor compliance and effectiveness',
          followups: [
            {
              id: 1,
              followup_type: 'progress_check',
              notes: 'Initial progress review - templates are being developed',
              status: 'satisfactory',
              followup_date: '2024-01-12',
              next_followup_date: '2024-01-26',
              created_by_name: 'Dr. Sarah Johnson',
              created_at: '2024-01-12T14:30:00Z'
            }
          ]
        },
        message: 'Recommendation details retrieved successfully'
      };
    },

    updateStatus: async (data: any) => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true as const,
        data: { id: data.recommendation_id, status: data.status },
        message: 'Recommendation status updated successfully'
      };
    },

    assignRecommendation: async (data: any) => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true as const,
        data: { id: data.recommendation_id, assigned_to: data.assigned_to },
        message: 'Recommendation assigned successfully'
      };
    },

    createFollowup: async (data: any) => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true as const,
        data: { id: Date.now(), ...data },
        message: 'Follow-up created successfully'
      };
    },

    bulkUpdate: async (data: any) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        success: true as const,
        data: { updated_count: data.recommendation_ids.length },
        message: 'Recommendations updated successfully'
      };
    },

    getFollowups: async (recommendationId: number) => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return {
        success: true as const,
        data: [
          {
            id: 1,
            recommendation_id: recommendationId,
            followup_type: 'progress_check',
            notes: 'Initial progress review - templates are being developed',
            status: 'satisfactory',
            followup_date: '2024-01-12',
            next_followup_date: '2024-01-26',
            created_by: 1,
            created_by_name: 'Dr. Sarah Johnson',
            created_at: '2024-01-12T14:30:00Z'
          },
          {
            id: 2,
            recommendation_id: recommendationId,
            followup_type: 'completion_verification',
            notes: 'Templates have been completed and are ready for implementation',
            status: 'satisfactory',
            followup_date: '2024-01-26',
            next_followup_date: null,
            created_by: 1,
            created_by_name: 'Dr. Sarah Johnson',
            created_at: '2024-01-26T10:15:00Z'
          }
        ],
        message: 'Follow-ups retrieved successfully'
      };
    },

    updateRecommendation: async (data: any) => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true as const,
        data: { id: data.id, ...data },
        message: 'Recommendation updated successfully'
      };
    },

    getOverdueRecommendations: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return {
        success: true as const,
        data: [
          {
            id: 2,
            title: 'Update communication protocols',
            description: 'Revise inter-departmental communication procedures',
            priority: 'critical',
            status: 'pending',
            category_name: 'Communication',
            category_color: '#F59E0B',
            assigned_to_name: 'Dr. Michael Chen',
            patient_id: 'P001235',
            days_overdue: 5
          }
        ],
        message: 'Overdue recommendations retrieved successfully'
      };
    },

    deleteRecommendation: async (id: number) => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        success: true as const,
        data: { id },
        message: 'Recommendation deleted successfully'
      };
    },

    bulkDeleteRecommendations: async (recommendationIds: number[]) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        success: true as const,
        data: { deleted_ids: recommendationIds },
        message: `Successfully deleted ${recommendationIds.length} recommendations`
      };
    }
  }
};

// Helper function to check if mock data should be used
export const shouldUseMockData = () => USE_MOCK_DATA;

// Development helper to reset mock data
export const resetMockData = () => {
  if (USE_MOCK_DATA) {
    console.log('🔄 Mock Data Service: Resetting mock data...');
    // In a real implementation, you might want to reset arrays to initial state
    // For now, we'll just log the action
    console.log('✅ Mock Data Service: Reset complete');
  }
};
